import { FieldType, FormUsage, StateForm } from './fieldTypes';

export interface FieldResponseDTO {
  id: number;
  fieldId: number;
  response?: string;
  fieldType: FieldType;
  order: number;
  title?: string;
  sourceItems?: string | null;
  filesNames?: FileType[] | null;
}

export interface DetailFormResponseFieldDTO {
  id: number;
  response?: string;
  isRequired: boolean;
  hideTitle: boolean;
  title?: string;
  description?: string;
  parametres?: string;
  valuesPath?: string;
  sourceItems?: string | null;
  fieldType: FieldType;
  filesContent?: Record<string, string>[] | null;
  files?: File[] | null;
  filesNames?: FileType[] | null;
}

export interface FormResponse {
  formId: number;
  title?: string;
  description?: string;
  iconPath?: string;
  statutId: number;
  responseId: number;
  isAnonymous: boolean;
  statuts?: SelectItem[];
  statut: SelectItem;
  userId?: string;
  usersList?: SelectItem[];
  fields?: FieldResponseDTO[];
  sharedId?: number;
}

export interface FieldsType {
  id: number;
  fieldType: FieldType;
  title: string;
  options: SelectItem[];
}

interface SocietyType {
  id?: number;
  title: string;
  country: string | null;
  fuseaHoraire: string | null;
  congeSyteme: string | null;
  totalUsers: number;
  address1: string | null;
  address2: string | null;
  city: string | null;
  updatedBy: string | null;
  updatedDate: string | null;
  countries: SelectItem[] | null;
  holidayCalender: string | null;
  previousId?: number;
  nextId?: number;
}

interface SiteType {
  id?: number;
  title: string;
  adressMap: string | null;
  country: string | null;
  fuseaHoraire: string | null;
  congeSyteme: string | null;
  totalUsers: number;
  placeLat: string | null;
  placeLng: string | null;
  placeId: string | null;
  coutries: SelectItem[] | null;
  holidayCalender: string | null;
  nextId?: number | null;
  previousId?: number | null;
}

export interface FormResponseSummary {
  id: number;
  title: string;
  createdBy: SelectItem;
  statePublished: string;
  activedStatut: boolean;
  usage: FormUsage;
  entries: number;
  state: StateForm;
  isArchived: boolean;
  isAnonymous: boolean;
  fields: Record<string, FieldsType>;
  statues: SelectItem[];
  sites: SiteType[];
  societies: SocietyType[];
}

export interface FormResponseFilter {
  start: number;
  length: number;
  search: {
    value: string;
    regex: boolean;
  };
  order: [
    {
      column: string;
      dir: string;
      custom: boolean;
    },
  ];
  filters: {
    dataSourceId: string;
    status: string | number[];
    site: string | string[];
    society: string | string[];
    dateMin: string;
    dateMax: string;
    dynamicFilters: Record<string, string>;
  };
}

export interface FormResponseListFilter {
  start: number;
  length: number;
  search: {
    value: string;
    regex: boolean;
  };
  order: [
    {
      column: string;
      dir: string;
      custom: boolean;
    },
  ];
  filters: {
    dataSourceId: string;
    status: string | number[];
    dateMin: string;
    dateMax: string;
    dynamicFilters: Record<string, string>;
  };
}

export interface FormResponsesType {
  data: string;
  draw: number;
  recordsFiltered: number;
  recordsTotal: number;
  visible: boolean;
}

export interface FormResponseDetail extends FormResponse {
  id: number;
  formId: number;
  createdBy: SelectItem;
  user: SelectItem;
  createdDate: string;
  formName: string;
  iconForm: string;
  statutList: SelectItem[];
  activedStatut: boolean;
  activedApproval: boolean;
  fields: FieldResponseDTO[];
  createBy: {
    id: string;
    fullName: string;
    fonctionName: string;
    image: string;
    date: string;
    approvelCount: number;
    approvelDone: number;
    approvelTaux: number;
  };
  approval: Array<{
    id: string;
    fullName: string;
    fonctionName: string;
    image: string;
    level: string;
    date: string | null;
    statut: SelectItem;
    status: string | null;
    raison: string | null;
  }>;
}

export interface DetailFormResponse {
  id: number;
  formId: number;
  createdBy: SelectItem;
  user: SelectItem;
  createdDate: string;
  formName: string;
  iconForm: string;
  statutList: SelectItem[];
  activedStatut: boolean;
  activedApproval: boolean;
  fields: DetailFormResponseFieldDTO[];
  createBy: {
    id: string;
    fullName: string;
    fonctionName: string;
    image: string;
    date: string;
    approvelCount: number;
    approvelDone: number;
    approvelTaux: number;
  };
  approval: Array<{
    id: string;
    fullName: string;
    fonctionName: string;
    image: string;
    level: string;
    date: string | null;
    statut: SelectItem;
    status: string | null;
    raison: string | null;
  }>;
  statut: SelectItem;
  title?: string;
  description?: string;
  iconPath?: string;
  statutId: number;
  responseId: number;
  isAnonymous: boolean;
  statuts?: SelectItem[];
  userId?: string;
  usersList?: SelectItem[];
  sharedId?: number;
}

export enum RolesClient {
  Proprietaire = 'Proprietaire',
  Administrateur = 'Administrateur',
  Responsable = 'Responsable',
  Employe = 'Employe',
  SupportMintHR = 'SupportMintHR',
}

export function isFieldResponseDTO(
  field: FieldResponseDTO | DetailFormResponseFieldDTO,
): field is FieldResponseDTO {
  return 'fieldId' in field && 'order' in field;
}

export function isDetailFormResponseFieldDTO(
  field: FieldResponseDTO | DetailFormResponseFieldDTO,
): field is DetailFormResponseFieldDTO {
  return 'isRequired' in field && 'hideTitle' in field;
}

export interface FormResponseItem {
  id: number;
  createdDate: string;
  formId: number;
  user: SelectItem & { imagePath: string };
  formName: string;
  statut: SelectItem & { selected: string; valueGroup: string };
}

export interface FieldResponseDTO {
  id: number;
  fieldId: number;
  responseId: number;
  response?: string;
  fieldType: FieldType;
  order: number;
  title?: string;
  sourceItems?: string | null;
  filesNames?: FileType[] | null;
}

export interface ResponseDetail {
  id: number;
  formId: number;
  createdBy: SelectItem;
  user: SelectItem;
  matricule: string | null;
  createdDate: string;
  statut: SelectItem;
  formName: string;
  iconForm: string;
  statutList: SelectItem[];
  activedStatut: boolean;
  activedApproval: boolean;
  fields: FieldResponseDTO[];
  createBy: {
    id: string;
    fullName: string;
    fonctionName: string;
    image: string;
    date: string;
    approvelCount: number;
    approvelDone: number;
    approvelTaux: number;
  } | null;
  approval: Array<{
    id: string;
    fullName: string;
    fonctionName: string;
    image: string;
    level: string;
    date: string | null;
    statut: SelectItem;
    status: string | null;
    raison: string | null;
  }> | null;
  isRefused: boolean;
  email: string | null;
}

export interface DetailResponseData {
  response: ResponseDetail;
  clientName: string;
  addresse: string | null;
  phoneNumber: string | null;
  city: string | null;
  country: string | null;
}

export interface ResponseExport {
  dataSourceId: string;
  ids: string[];
  idsNotSelected: string[];
  status: string | number[];
  dateMin: string;
  dateMax: string;
  site: string | string[];
  society: string | string[];
  dynamicFilters: Record<string, string>;
}
